import {useLocation, useParams} from 'react-router-dom'
import {useQuery} from 'react-query'
import {parseISO} from 'date-fns'

import {
  api,
  queryClient,
  useMutationQuery,
  replaceQueryClientDataItem,
} from 'base'
import {showSuccessNotification} from 'common/helpers'
import {ForecastItem} from 'common/types'

const VARIATION_REQUEST_ROUTE_URL = '/requests/variation/'
const rebuildPayload = (payload:any, pathname?:string, uuid?:string) => (
  ( 
    pathname?.includes(VARIATION_REQUEST_ROUTE_URL) && 
    (!!uuid)
  ) ? {
    ...payload,
    variationRequest: uuid
  } : payload
)

export const useGetForecast = (projectUuid: string, enabled: boolean = true) =>
  useQuery<ForecastItem[]>(
    ['forecast', projectUuid],
    async () => {
      const {data} = await api.get('/forecastItem/list', {
        params: {
          include: ['drawdown', 'values', 'transactions'],
          project: projectUuid,
        },
      })
      const sortedData = data.data.slice()
      sortedData.sort(
        (a: any, b: any) => +parseISO(a.value.date) - +parseISO(b.value.date)
      )
      return sortedData
    },
    {enabled}
  )

export const useCreateForecast = (
  projectUuid: string,
  onClose: () => void,
  setForecastCreatedProjects: (projectUuid: string) => void
) =>
  useMutationQuery<any, any, any>(
    ['createForecast', projectUuid],
    async (forecast) => {
      const {data} = await api.post('/forecast/create', forecast)
      return data
    },
    {
      onSuccess() {
        setForecastCreatedProjects(projectUuid)
        queryClient.invalidateQueries('request')
        queryClient.invalidateQueries('forecast')
        showSuccessNotification('Forecast has been created.')
        onClose()
      },
    }
  )

export const useUpdateForecast = (
  projectUuid: string,
  forecastItemUuid?: string,
  onSuccessActions?: () => void
) => {
  const {pathname}  = useLocation()
  const {uuid}      = useParams()
  return useMutationQuery<any, any, any>(
    ['updateForecast', projectUuid, forecastItemUuid],
    async (payload) => {
      const {data} = await api.post(
        '/forecast/update',
        rebuildPayload(payload, pathname, uuid)
      )
      return data
    },
    {
      onSuccess() {
        queryClient.invalidateQueries('request')
        queryClient.invalidateQueries('forecast')
        queryClient.invalidateQueries('variation-request')
        queryClient.invalidateQueries('variation-schedule-info')
        onSuccessActions && onSuccessActions()
        showSuccessNotification('Forecast has been updated.')
      },
    }
  )
}

export const useCreateForecastItem = (
  projectUuid: string,
  onClose: () => void
) => {
  const {pathname}  = useLocation()
  const {uuid}      = useParams()
  return useMutationQuery<ForecastItem>(
    ['createForecastItem', projectUuid],
    async (payload) => {
      const {data} = await api.post(
        '/forecastItem/new',
        rebuildPayload(payload, pathname, uuid)
      )
      return data.data
    },
    {
      onSuccess(data) {
        const requestKey = ['forecast', projectUuid]
        queryClient.setQueriesData(requestKey, (oldData: ForecastItem[]) => {
          const newSortedData = oldData.slice()
          newSortedData.push(data)
          newSortedData.sort((a, b) => +parseISO(a.date) - +parseISO(b.date))
          return newSortedData
        })
        queryClient.invalidateQueries(requestKey)
        queryClient.invalidateQueries('variation-request')
        queryClient.invalidateQueries('variation-schedule-info')
        onClose()
      },
    }
  )
}

export const useUpdateForecastItem = (
  projectUuid: string,
  forecastItemUuid: string,
  onClose?: () => void
) => {
  return useMutationQuery<ForecastItem, any, any>(
    ['updateForecastItem', forecastItemUuid],
    async (payload) => {
      const {data} = await api.post(
        `/forecastItem/${forecastItemUuid}`,
        payload
      )
      return data.data
    },
    {
      onSuccess(data) {
        const requestKey = ['forecast', projectUuid]
        replaceQueryClientDataItem(requestKey, data)
        queryClient.invalidateQueries(requestKey)
        queryClient.invalidateQueries('variation-request')
        queryClient.invalidateQueries('variation-schedule-info')
        onClose && onClose()
      },
    }
  )
}

/* export const useDeleteForecastItem = (
  projectUuid: string,
  forecastItemUuid: string
) =>
  useMutationQuery<ForecastItem>(
    ['deleteForecastItem', forecastItemUuid],
    async () => {
      const {data} = await api.delete(`/forecastItem/${forecastItemUuid}`)
      return data.data
    },
    {
      onSuccess() {
        const requestKey = ['forecast', projectUuid]
        queryClient.setQueriesData(requestKey, (oldData: ForecastItem[]) =>
          oldData.filter((item) => item.uuid !== forecastItemUuid)
        )
        queryClient.invalidateQueries(requestKey)
      },
    }
  ) */
